import React from 'react';
import classNames from 'classnames';

/**
 * Astra loading indicator — an "eye" emblem (the viewer symbol) above a modern
 * rounded progress bar.
 *
 * - without `progress`: indeterminate (a gradient slug sweeps the track)
 * - with `progress` (0..100): determinate fill
 * - `textBlock` renders below the bar (e.g. "Loaded 40 of 120 images")
 */
function LoadingIndicatorProgress({ className, textBlock, progress }) {
  const determinate = progress !== undefined && progress !== null;

  return (
    <div
      className={classNames(
        'absolute top-0 left-0 z-50 flex flex-col items-center justify-center gap-5',
        className
      )}
    >
      {/* Self-contained keyframes for the sweep + soft blink. */}
      <style>{`
        @keyframes astra-bar-sweep { 0% { transform: translateX(-120%); } 100% { transform: translateX(320%); } }
        @keyframes astra-eye-blink { 0%, 92%, 100% { transform: scaleY(1); } 96% { transform: scaleY(0.08); } }
      `}</style>

      {/* Eye emblem */}
      <div className="relative">
        <svg
          viewBox="0 0 64 40"
          className="h-12 w-[76px]"
          fill="none"
          aria-hidden="true"
          style={{
            animation: 'astra-eye-blink 3.2s ease-in-out infinite',
            transformOrigin: '50% 50%',
          }}
        >
          {/* lids */}
          <path
            d="M3 20C10 8.5 20.5 3 32 3s22 5.5 29 17c-7 11.5-17.5 17-29 17S10 31.5 3 20Z"
            stroke="hsl(var(--primary))"
            strokeWidth="2.4"
            strokeLinejoin="round"
          />
          {/* iris */}
          <circle
            cx="32"
            cy="20"
            r="9.5"
            stroke="hsl(var(--highlight))"
            strokeWidth="2.4"
          />
          <circle
            cx="32"
            cy="20"
            r="4.4"
            fill="hsl(var(--primary))"
          />
          {/* catchlight */}
          <circle
            cx="35.2"
            cy="16.6"
            r="1.6"
            fill="white"
            opacity="0.9"
          />
        </svg>
        {/* soft glow */}
        <div className="bg-primary/25 absolute inset-0 -z-10 rounded-full blur-2xl" />
      </div>

      {/* Modern progress bar */}
      <div className="bg-foreground/10 ring-foreground/10 relative h-[6px] w-60 overflow-hidden rounded-full ring-1 ring-inset">
        {determinate ? (
          <div
            className="from-primary to-highlight h-full rounded-full bg-gradient-to-r transition-[width] duration-300 ease-out"
            style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
          />
        ) : (
          <div
            className="from-primary/0 via-primary to-highlight absolute top-0 h-full w-2/5 rounded-full bg-gradient-to-r"
            style={{ animation: 'astra-bar-sweep 1.3s ease-in-out infinite' }}
          />
        )}
      </div>

      {textBlock}
    </div>
  );
}

export default LoadingIndicatorProgress;
